import {
  Alert,
  Box,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { extractErrorMessage } from '../../core/services/api';
import { resources } from '../../core/services/resources';
import type { LineOfBiz } from '../../core/types/data-models';

interface EditIdustriesDialogProps {
  open: boolean;
  lineOfBizId: number | null;
  initialValue: string;
  onClose: () => void;
}

/**
 * Free-form Markdown editor for `LineOfBiz.industries`. Two modes:
 *   - Edit  → multiline TextField, monospaced, full markdown text.
 *   - View  → rendered Markdown preview using the same styles as
 *             the dashboard's inline display.
 *
 * Save PUTs the LOB row with the new value; Cancel discards.
 */
export function EditIdustriesDialog({
  open,
  lineOfBizId,
  initialValue,
  onClose,
}: EditIdustriesDialogProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'edit' | 'view'>('edit');
  const [text, setText] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  // Reseed on each open so the dialog shows the latest LOB value and
  // discards any unsaved typing from a prior cancelled session.
  useEffect(() => {
    if (!open) return;
    setText(initialValue);
    setMode('edit');
    setError(null);
  }, [open, initialValue]);

  const save = useMutation({
    mutationFn: (markdown: string) =>
      resources.linesOfBusiness.update(lineOfBizId as number, {
        industries: markdown,
      } as Partial<LineOfBiz>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lines-of-business'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (lineOfBizId == null) return;
    setError(null);
    await save.mutateAsync(text);
  };

  return (
    <FormDialog
      open={open}
      title="Edit Industries"
      submitLabel={save.isPending ? 'Saving…' : 'Save'}
      loading={save.isPending}
      maxWidth="md"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
      titleActions={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next) => {
            if (next) setMode(next);
          }}
        >
          <ToggleButton value="edit">Edit</ToggleButton>
          <ToggleButton value="view">View</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}

      {mode === 'edit' ? (
        <TextField
          autoFocus
          fullWidth
          multiline
          // Cap the visible rows so the dialog never outgrows the
          // viewport — the textarea scrolls internally past this.
          minRows={14}
          maxRows={18}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'## Industry name\n - Segment - short description'}
          InputProps={{
            sx: {
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.5,
              // Disable text wrapping so long lines scroll
              // horizontally instead of pushing the editor taller.
              '& textarea': {
                whiteSpace: 'pre',
                overflowX: 'auto',
                overflowY: 'auto',
              },
            },
          }}
        />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            minHeight: 280,
            // Bound the preview so very long Markdown stays inside the
            // dialog; both axes scroll when content overflows.
            maxHeight: '60vh',
            overflow: 'auto',
            backgroundColor: 'background.default',
            '& h2': {
              fontSize: '0.95rem',
              fontWeight: 700,
              mt: 1.5,
              mb: 0.5,
              '&:first-of-type': { mt: 0 },
            },
            '& ul': { my: 0.5, pl: 3 },
            '& li': { mb: 0.25 },
            '& p': { my: 0.5 },
            // Preserve long inline tokens (URLs, code) — render them
            // on a single line so the horizontal scrollbar kicks in.
            '& pre, & code': { whiteSpace: 'pre' },
            fontSize: '0.875rem',
            lineHeight: 1.5,
          }}
        >
          {text.trim().length === 0 ? (
            <Box sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              Nothing to preview yet.
            </Box>
          ) : (
            <ReactMarkdown>{text}</ReactMarkdown>
          )}
        </Paper>
      )}
    </FormDialog>
  );
}
